import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme
} from '@mui/material';
import { 
  Upload as UploadIcon,
  Download as DownloadIcon,
  QrCodeScanner as QrCodeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

function BinAuditApp() {
  const theme = useTheme();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [scannedCodes, setScannedCodes] = useState(new Set());
  const [manualInput, setManualInput] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [binData, setBinData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedPlanogram, setSelectedPlanogram] = useState('');
  const [planograms, setPlanograms] = useState([]);
  const [loadingPlanograms, setLoadingPlanograms] = useState(false);
  const [scannedByData, setScannedByData] = useState(new Map()); // Map of binId -> user info
  const [status, setStatus] = useState({ message: "", type: "info" });
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [showMissingOnly, setShowMissingOnly] = useState(false);

  // Load planograms from API
  const loadPlanograms = async () => {
    setLoadingPlanograms(true);
    try {
      const response = await fetch(`${config.apiUrl}/planograms/list/dropdown`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlanograms(data);
        if (data.length > 0) {
          setSelectedPlanogram(data[0].id);
        }
      } else {
        setStatus({ message: "Failed to load planograms", type: "error" });
      }
    } catch (error) {
      setStatus({ message: "Error loading planograms: " + error.message, type: "error" });
    } finally {
      setLoadingPlanograms(false);
    }
  };

  // Load selected planogram bins
  const loadPlanogramBins = async (planogramId) => {
    if (!planogramId) return;
    
    try {
      const response = await fetch(`${config.apiUrl}/planograms/${planogramId}/bins`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBinData(data.bins);
        setStatus({ message: `Loaded ${data.bins.length} bins from ${data.name}`, type: "success" });
      } else {
        setStatus({ message: "Failed to load planogram bins", type: "error" });
      }
    } catch (error) {
      setStatus({ message: "Error loading planogram bins: " + error.message, type: "error" });
    }
  };

  // Initialize
  useEffect(() => {
    setStatus({ message: "Select a planogram to start auditing bins.", type: "info" });
    
    // Load planograms
    loadPlanograms();
  }, []);

  const setStatusMessage = (msg, type = "info") => {
    setStatus({ message: msg, type });
  };


  // File upload and parsing
  const readCSV = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          
          // Debug: Log the headers to see what we're working with
          console.log('CSV Headers found:', headers);
          
          const data = lines.slice(1).map(line => {
            const cells = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                cells.push(current.trim().replace(/"/g, ''));
                current = '';
              } else {
                current += char;
              }
            }
            cells.push(current.trim().replace(/"/g, ''));
            
            const row = {};
            headers.forEach((header, index) => {
              row[header] = cells[index] || '';
            });
            return row;
          });
          
          // Debug: Log first few rows to see the data structure
          console.log('First few rows of data:', data.slice(0, 3));
          
          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const handleFileUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setStatusMessage("Choose a file first.", "error");
      return;
    }

    setStatusMessage("Reading CSV file...", "info");
    
    try {
      const data = await readCSV(file);
      setBinData(data);
      
      // Extract unique planograms - try different possible column names
      let planogramColumn = 'Planogram';
      const possiblePlanogramColumns = ['Planogram', 'planogram', 'PLANOGRAM', 'Planogram ', 'Planogram Name'];
      
      // Find the actual planogram column name
      for (const colName of possiblePlanogramColumns) {
        if (data.length > 0 && data[0].hasOwnProperty(colName)) {
          planogramColumn = colName;
          break;
        }
      }
      
      console.log('Using planogram column:', planogramColumn);
      
      const uniquePlanograms = [...new Set(data.map(row => row[planogramColumn]))].filter(Boolean);
      console.log('Found planograms:', uniquePlanograms);
      
      setPlanograms(uniquePlanograms);
      
      if (uniquePlanograms.length > 0) {
        setSelectedPlanogram(uniquePlanograms[0]);
      }
      
      setStatusMessage(`Loaded ${data.length} bins from ${file.name}`, "success");
    } catch (e) {
      console.error(e);
      setStatusMessage("Failed to read CSV file: " + e.message, "error");
    }
  };

  // Load bins when planogram is selected
  useEffect(() => {
    if (selectedPlanogram) {
      loadPlanogramBins(selectedPlanogram);
      loadScans(selectedPlanogram);
    }
  }, [selectedPlanogram]);

  // Filter data based on selected planogram
  useEffect(() => {
    if (binData.length > 0) {
      let filtered = binData;
      
      if (showMissingOnly) {
        filtered = filtered.filter(row => !scannedCodes.has(row.slnPositionId));
      }
      
      setFilteredData(filtered);
    }
  }, [binData, showMissingOnly, scannedCodes]);

  // Strip "*BN" prefix from barcode
  const cleanBarcode = (barcode) => {
    if (barcode.startsWith('*BN')) {
      return barcode.substring(3); // Remove "*BN" prefix
    }
    return barcode;
  };

  // Process a single scan
  const processScan = async (rawBinId) => {
    const binId = cleanBarcode(rawBinId.trim());
    
    if (scannedCodes.has(binId)) {
      setStatusMessage(`Bin ${binId} already scanned`, "warning");
      return false;
    }

    // Check if bin exists in current planogram
    const binExists = binData.some(row => row.slnPositionId === binId);
    if (!binExists) {
      setStatusMessage(`Bin ${binId} not found in current planogram`, "error");
      return false;
    }

    try {
      // Save scan to database
      const response = await fetch(`${config.apiUrl}/scans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planogramId: selectedPlanogram,
          binId: binId,
          location: planograms.find(p => p.id === selectedPlanogram)?.location || '',
          planogramName: planograms.find(p => p.id === selectedPlanogram)?.name || ''
        })
      });

      if (response.ok) {
        const newScannedCodes = new Set([...scannedCodes, binId]);
        setScannedCodes(newScannedCodes);
        
        // Reload scans to get updated user info from database
        loadScans(selectedPlanogram);
        
        setStatusMessage(`Scanned bin ${binId}`, "success");
        return true;
      } else {
        setStatusMessage(`Failed to save scan for bin ${binId}`, "error");
        return false;
      }
    } catch (error) {
      setStatusMessage(`Error saving scan: ${error.message}`, "error");
      return false;
    }
  };

  // Load scans from database
  const loadScans = async (planogramId) => {
    if (!planogramId) return;
    
    try {
      const response = await fetch(`${config.apiUrl}/scans/planogram/${planogramId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const scans = await response.json();
        const scannedBinIds = new Set(scans.map(scan => scan.binId));
        setScannedCodes(scannedBinIds);
        
        // Create map of binId -> user info
        const scannedByMap = new Map();
        scans.forEach(scan => {
          scannedByMap.set(scan.binId, {
            name: `${scan.scannedBy?.firstName || ''} ${scan.scannedBy?.lastName || ''}`.trim() || scan.scannedBy?.username || 'Unknown',
            username: scan.scannedBy?.username || 'Unknown',
            scannedAt: scan.scannedAt
          });
        });
        setScannedByData(scannedByMap);
        
        setStatus({ message: `Loaded ${scans.length} previously scanned bins`, type: "success" });
      }
    } catch (error) {
      console.error('Error loading scans:', error);
    }
  };

  // Scanning functions
  const handleManualScan = async () => {
    if (!manualInput.trim()) {
      setStatusMessage("Enter a bin ID to scan.", "error");
      return;
    }

    await processScan(manualInput);
    setManualInput('');
  };

  const handleBulkScan = async () => {
    if (!manualInput.trim()) {
      setStatusMessage("Enter bin IDs to scan.", "error");
      return;
    }

    const binIds = manualInput.split('\n').map(id => id.trim()).filter(Boolean);
    let addedCount = 0;
    let notFoundCount = 0;
    let errorCount = 0;

    for (const rawBinId of binIds) {
      const binId = cleanBarcode(rawBinId);
      
      if (!scannedCodes.has(binId)) {
        // Check if bin exists in current planogram
        const binExists = binData.some(row => row.slnPositionId === binId);
        if (binExists) {
          const success = await processScan(rawBinId);
          if (success) {
            addedCount++;
          } else {
            errorCount++;
          }
        } else {
          notFoundCount++;
        }
      }
    }
    
    let message = `Scanned ${addedCount} new bins`;
    if (notFoundCount > 0) message += `, ${notFoundCount} not found in planogram`;
    if (errorCount > 0) message += `, ${errorCount} errors`;
    setStatusMessage(message, "success");
    setManualInput('');
  };

  // Auto-scan when input changes (for rapid scanning)
  const handleInputChange = (event) => {
    const value = event.target.value;
    setManualInput(value);
    
    // Auto-scan when Enter is pressed or when a complete barcode is detected
    if (value.includes('\n') || value.includes('\r') || (value.length > 10 && value.includes('*BN'))) {
      const cleanValue = value.replace(/[\n\r]/g, '').trim();
      if (cleanValue) {
        setManualInput(cleanValue);
        // Small delay to ensure the input is set, then auto-scan
        setTimeout(async () => {
          await processScan(cleanValue);
          setManualInput('');
        }, 100);
      }
    }
  };

  const clearScanned = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/scans/my-scans`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planogramId: selectedPlanogram
        })
      });

      if (response.ok) {
        const emptySet = new Set();
        setScannedCodes(emptySet);
        setScannedByData(new Map());
        setStatusMessage("Cleared all scanned bins", "info");
      } else {
        setStatusMessage("Failed to clear scanned bins", "error");
      }
    } catch (error) {
      setStatusMessage(`Error clearing scans: ${error.message}`, "error");
    }
  };

  // Export functions
  const exportMissingBins = () => {
    if (!selectedPlanogram) {
      setStatusMessage("Select a planogram first.", "error");
      return;
    }

    const missingBins = binData.filter(row => !scannedCodes.has(row.slnPositionId));

    if (missingBins.length === 0) {
      setStatusMessage("No missing bins found!", "success");
      return;
    }

    const csvContent = [
      ['SLN Position ID', 'Fastenal Part #', 'Customer Part #', 'SKU Description', 'Bin Row', 'Bin Column', 'Min Qty', 'Max Qty'],
      ...missingBins.map(row => [
        row.slnPositionId,
        row.fastenalPart,
        row.customerPart,
        row.skuDescription,
        row.binRow,
        row.binColumn,
        row.minQty,
        row.maxQty
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `missing_bins_${selectedPlanogram}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    setStatusMessage(`Exported ${missingBins.length} missing bins`, "success");
  };

  // Calculate statistics
  const totalBins = filteredData.length;
  const scannedBins = filteredData.filter(row => scannedCodes.has(row.slnPositionId)).length;
  const missingBins = totalBins - scannedBins;

  // Pagination
  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <Box sx={{ p: 3, height: '100vh', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <QrCodeIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Bin Audit Scanner
        </Typography>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a planogram from the pre-loaded data and start scanning bins to identify missing ones.
      </Typography>

      {status.message && (
        <Alert 
          severity={status.type === "error" ? "error" : status.type === "success" ? "success" : status.type === "warning" ? "warning" : "info"}
          sx={{ mb: 3 }}
        >
          {status.message}
        </Alert>
      )}

      {/* Planogram Selection Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Select Planogram" />
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Planogram</InputLabel>
                <Select
                  value={selectedPlanogram}
                  onChange={(e) => setSelectedPlanogram(e.target.value)}
                  label="Planogram"
                  disabled={loadingPlanograms || planograms.length === 0}
                >
                  {planograms.map(planogram => (
                    <MenuItem key={planogram.id} value={planogram.id}>
                      {planogram.name} ({planogram.customer} - {planogram.location})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={`Total: ${planograms.length}`} 
                  color="primary" 
                  size="small" 
                />
                {selectedPlanogram && (
                  <Chip 
                    label={`Bins: ${binData.length}`} 
                    color="secondary" 
                    size="small" 
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Scanning Section */}
      {selectedPlanogram && (
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title="Scan Bins" 
            subheader="Rapid scanning mode: Just scan barcodes and they'll be processed automatically"
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Scan Bin ID (Auto-scan enabled)"
                  value={manualInput}
                  onChange={handleInputChange}
                  placeholder="Scan barcode or enter bin ID manually. Auto-scans when *BN prefix detected or Enter pressed."
                  variant="outlined"
                  helperText="*BN prefix will be automatically removed. Just scan and go!"
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleManualScan}
                    disabled={!manualInput.trim()}
                    size="small"
                  >
                    Scan Single
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleBulkScan}
                    disabled={!manualInput.trim()}
                    size="small"
                  >
                    Scan Multiple
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Scanning Progress
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip label={`Scanned: ${scannedBins}`} color="success" size="small" />
                      <Chip label={`Missing: ${missingBins}`} color="error" size="small" />
                      <Chip label={`Total: ${totalBins}`} color="primary" size="small" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {missingBins === 0 ? "All bins scanned! 🎉" : `${missingBins} bins remaining to scan`}
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="contained"
                    startIcon={<QrCodeIcon />}
                    onClick={() => setShowScanner(true)}
                    fullWidth
                  >
                    Open QR Scanner
                  </Button>
                  
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={`Scanned: ${scannedBins}`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      icon={<ErrorIcon />}
                      label={`Missing: ${missingBins}`}
                      color="error"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Bin Table */}
      {filteredData.length > 0 && (
        <Card>
          <CardHeader 
            title={`Bin Data - ${planograms.find(p => p.id === selectedPlanogram)?.name || 'Unknown Planogram'} (${totalBins} total bins)`}
            subheader={planograms.find(p => p.id === selectedPlanogram) ? 
              `${planograms.find(p => p.id === selectedPlanogram).customer} - ${planograms.find(p => p.id === selectedPlanogram).location}` : 
              'Select a planogram to view bin data'
            }
            action={
              <Button
                variant={showMissingOnly ? "contained" : "outlined"}
                startIcon={<VisibilityIcon />}
                onClick={() => setShowMissingOnly(!showMissingOnly)}
                size="small"
              >
                {showMissingOnly ? "Show All" : "Show Missing Only"}
              </Button>
            }
          />
          <CardContent>
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>SLN Position ID</strong></TableCell>
                    <TableCell><strong>Fastenal Part #</strong></TableCell>
                    <TableCell><strong>Customer Part #</strong></TableCell>
                    <TableCell><strong>SKU Description</strong></TableCell>
                    <TableCell><strong>Bin Location</strong></TableCell>
                    <TableCell><strong>Scanned By</strong></TableCell>
                    <TableCell><strong>Min Qty</strong></TableCell>
                    <TableCell><strong>Max Qty</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentData.map((row, index) => {
                    const isScanned = scannedCodes.has(row.slnPositionId);
                    return (
                      <TableRow 
                        key={index} 
                        hover
                        sx={{ 
                          backgroundColor: isScanned ? 'success.light' : 'error.light',
                          opacity: isScanned ? 0.7 : 1
                        }}
                      >
                        <TableCell>
                          {isScanned ? (
                            <Chip icon={<CheckCircleIcon />} label="Scanned" color="success" size="small" />
                          ) : (
                            <Chip icon={<ErrorIcon />} label="Missing" color="error" size="small" />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {row.slnPositionId}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {row.fastenalPart}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {row.customerPart}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap>
                            {row.skuDescription}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.binRow}×{row.binColumn}
                        </TableCell>
                        <TableCell>
                          {isScanned && scannedByData.has(row.slnPositionId) ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {scannedByData.get(row.slnPositionId).name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(scannedByData.get(row.slnPositionId).scannedAt).toLocaleString()}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not scanned
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{row.minQty}</TableCell>
                        <TableCell>{row.maxQty}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={filteredData.length}
              page={currentPage}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[50]}
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </CardContent>
        </Card>
      )}

      {/* QR Scanner Dialog */}
      <Dialog open={showScanner} onClose={() => setShowScanner(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          QR Code Scanner
          <IconButton
            onClick={() => setShowScanner(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <QrCodeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              QR Scanner - Coming Soon
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Camera QR scanning will be implemented in a future update. 
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>For now:</strong> Use the manual input field above with your Bluetooth scanner, 
              or paste multiple bin IDs for bulk scanning.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowScanner(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BinAuditApp;
